import React, { useEffect } from 'react'

import { useCameraValidationTest } from '../hooks/useCameraValidationTest'
import { useEditCamera } from '../hooks/useEditCamera'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLiveStreamSetup } from '@/hooks/useLiveStreamSetup'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Box, GridItem, Spacer, Tooltip } from '@chakra-ui/react'

import {
  Button,
  FormInputControl,
  FormInputSelectControl,
  SkeletonModalBody,
} from '@/components/ui'

import { EditDeviceModalSection } from '../../components/EditDeviceModalSection'
import { FourColumnGrid } from '../../components/FourColumnGrid'
import { SectionHeader } from '../../components/SectionHeader'
import { TwoColumnGrid } from '../../components/TwoColumnGrid'
import { CameraFormDataTypeI } from '../../types/types'
import { isPlaced } from '../../utils/utils'
import { ValidateCameraStreamResults } from '../components/ValidateCameraStreamResults'
import { getCameraInputs } from '../utils/utils'
import { EditCameraLiveStreamingFields } from './EditCameraLiveStreamingFields'

interface EditCameraFormIProps {
  deviceId: string
  shouldRequireAuthentication: boolean
  onRequiresAuthenticationChange: (isChecked: boolean) => void
  onLoadingChange: (loading: boolean) => void
}

export const EditCameraForm = ({
  deviceId,
  shouldRequireAuthentication,
  onRequiresAuthenticationChange,
  onLoadingChange,
}: EditCameraFormIProps) => {
  const form = useForm<CameraFormDataTypeI>({
    mode: 'onChange',
  })

  const hasCameraTestFeatureFlag = useFeatureFlag(FeatureFlag.cameraTestUtil)
  const {
    handleSubmit,
    errors,
    register,
    control,
    setValue,
    reset,
    formState: { dirtyFields, isDirty },
  } = form

  const { isOrangePeel } = useLiveStreamSetup()
  const { shouldEnableUpdateDevice } = usePermissions()
  const {
    handleSetLiveStreamFields,
    shouldShowLiveStreamFields,
    cameraData,
    isLoading,
    floorsOptions,
    isUpdateDeviceLoading,
    isUpdateCameraLoading,
    updateCamera,
  } = useEditCamera(deviceId)

  const cameraValidationTest = useCameraValidationTest()
  const { showSuccess, showError } = useToasts()

  const onSubmit: SubmitHandler<CameraFormDataTypeI> = async (values) => {
    const formData = {
      id: deviceId,
      name: values.name,
      floorId: values.location?.value,
      manufacturerName: values.manufacturerName,
    }
    const cameraData = getCameraInputs(deviceId, values)

    try {
      await updateCamera(formData, cameraData, dirtyFields)
      showSuccess('Camera updated.')

      cameraValidationTest.reset()
    } catch (e) {
      // TODO - Look into a more helpful error. e.x. when testing I put in "Jack's test camera" and instead of
      //  telling me that I couldn't have a ' it said "Something went wrong"...
      //  Could look to do a regex matching like we use for MAC address?
      showError()
    } finally {
      // Reset the form dirty fields
      reset(values)
    }
  }

  const onValidateCameraClick = () => {
    cameraValidationTest.validateCameraStream(deviceId)
  }

  useEffect(() => {
    if (!isLoading) {
      setValue('name', cameraData?.device?.name, { shouldValidate: true })
      setValue(
        'location',
        floorsOptions?.find((i) => i.value === cameraData?.device?.floor?.id),
        {
          shouldValidate: true,
        }
      )
      setValue('externalId', cameraData?.device?.camera?.externalId)
      setValue('manufacturerName', cameraData?.device?.manufacturerName)
    }
  }, [isLoading, floorsOptions])

  const hasCameraTestResults = cameraValidationTest.finalResults !== undefined

  // This is a working solution that scrolls to the bottom of the Camera Test results.
  // Would be nice to get to smoothly scroll & animate, but not going to have that hold this up
  useEffect(() => {
    if (hasCameraTestResults) {
      const modalBody = document.querySelector('#editCameraModalBody')
      if (modalBody) {
        modalBody.scrollTop = modalBody.scrollHeight
      }
    }
  }, [hasCameraTestResults])

  useEffect(() => {
    // Update parent component's loading state
    onLoadingChange(isLoading)
  }, [cameraData, isLoading])

  return (
    <Box>
      {!cameraData || isLoading ? (
        <SkeletonModalBody />
      ) : (
        <EditDeviceModalSection mb={6}>
          <TwoColumnGrid>
            <GridItem>
              <SectionHeader
                subTitle='Configure the basic device information and connections settings for this device '
                title='Device Details & Connection'
              />
            </GridItem>
            <GridItem justifySelf='end'>
              <Button
                isDisabled={!shouldEnableUpdateDevice || !isDirty}
                isLoading={isUpdateDeviceLoading || isUpdateCameraLoading}
                loadingText='Saving Changes'
                onClick={handleSubmit(onSubmit)}
              >
                Save
              </Button>
            </GridItem>
          </TwoColumnGrid>

          <form>
            <FourColumnGrid>
              <GridItem>
                <FormInputControl
                  data-testid='devicesPage_editCameraModal_name'
                  errorMessage={errors.name && errors.name.message}
                  id='name'
                  inputRef={register({
                    required: 'Device Name is required',
                  })}
                  isInvalid={!!errors.name}
                  label='Device Name'
                  placeholder='Enter the Device Name'
                />
              </GridItem>
              <GridItem>
                <FormInputSelectControl
                  control={control}
                  data-testid='devicesPage_editCameraModal_location'
                  defaultValue={null}
                  errorMessage='Select a device location'
                  id='location'
                  isClearable
                  isDisabled={isLoading || isPlaced(cameraData?.device)}
                  isInvalid={!!errors?.location}
                  label='Device Location'
                  options={floorsOptions}
                  placeholder='Select Device Location'
                  rules={{ required: true }}
                  tooltipText={
                    isPlaced(cameraData?.device) &&
                    'Device must not be placed to update device location'
                  }
                />
              </GridItem>
              <GridItem>
                <FormInputControl
                  data-testid='devicesPage_editCameraModal_manufacturerName'
                  id='manufacturerName'
                  inputRef={register()}
                  label='Device Manufacturer'
                  placeholder='Enter Name'
                />
              </GridItem>
              <GridItem>
                <FormInputControl
                  data-testid='devicesPage_editCameraModal_externalId'
                  errorMessage={errors.externalId?.message}
                  id='externalId'
                  inputRef={register()}
                  label='External ID'
                  placeholder='Enter External ID'
                />
              </GridItem>
            </FourColumnGrid>
            {shouldShowLiveStreamFields && (
              <EditCameraLiveStreamingFields
                cameraData={cameraData}
                form={form}
                isLoading={isLoading}
                onRequiresAuthenticationChange={onRequiresAuthenticationChange}
                shouldRequireAuthentication={shouldRequireAuthentication}
              />
            )}
            {!shouldShowLiveStreamFields && isOrangePeel && (
              <Box display='flex' justifyContent='center' p='20px'>
                <Button
                  data-testid='devicesPage_editCameraModal_liveStreamingButton'
                  onClick={handleSetLiveStreamFields}
                >
                  Set Live Streaming
                </Button>
              </Box>
            )}
          </form>
          {hasCameraTestFeatureFlag && hasCameraTestResults && (
            <ValidateCameraStreamResults
              deviceId={deviceId}
              errorMessages={cameraValidationTest.finalResults.errorMessages}
              url={cameraValidationTest.finalResults.url}
            />
          )}
          <Box py='4'>
            {hasCameraTestFeatureFlag && (
              <>
                <Tooltip
                  hasArrow
                  isDisabled={cameraValidationTest.isTestingCamera}
                  label={
                    isDirty
                      ? 'Save changes before testing'
                      : 'Test camera config'
                  }
                  placement='left'
                >
                  <span>
                    <Button
                      isDisabled={isDirty}
                      isLoading={cameraValidationTest.isTestingCamera}
                      onClick={onValidateCameraClick}
                      paddingX='32px'
                      variant='outline'
                    >
                      Test
                    </Button>
                  </span>
                </Tooltip>

                <Spacer />
              </>
            )}
          </Box>
        </EditDeviceModalSection>
      )}
    </Box>
  )
}
